import { differenceInCalendarDays, differenceInMonths } from 'date-fns';
import AppRoutes from '@/const/routes';
import { ActionRequired, ActionRequiredType } from '@/interfaces';
import {
	getActionRequiredComplementByType,
	getActionRequiredPrefixByType,
} from '@/lib/actionRequiredPrefixLabel';

const DOCUMENT_ACTION_TYPES = ['DOCUMENT', 'REQUESTED_DOCUMENT'] as const;

export const hasDocumentRequiredAction = (items?: ActionRequired[]): boolean =>
	(items ?? []).some((item) =>
		DOCUMENT_ACTION_TYPES.includes(
			item.action_type as (typeof DOCUMENT_ACTION_TYPES)[number]
		)
	);

export const hasCompleteProfileAction = (items?: ActionRequired[]): boolean =>
	(items ?? []).some((item) => item.action_type === 'COMPLETE_PROFILE');

export const VERIFICATION_PHASE_MAX_ORDER = 3;

export const qualifiesForVerificationPhase = (
	hasDocumentAction: boolean,
	numericalOrder?: number
): boolean =>
	hasDocumentAction ||
	(numericalOrder !== undefined &&
		numericalOrder < VERIFICATION_PHASE_MAX_ORDER);

export const invalidRoutes = [
	AppRoutes.LAWYERAPP_CLIENT_PROFILE,
	AppRoutes.LAWYERAPP_CASES,
];

export const getModifiedUrl = (
	url: string,
	params?: Record<string, string>
): string => {
	if (!url) return '#';

	try {
		const urlObj = new URL(url, window.location.origin);

		if (params && Object.keys(params).length > 0) {
			Object.entries(params).forEach(([key, value]) => {
				urlObj.searchParams.set(key, value);
			});
		}

		return urlObj.pathname + urlObj.search;
	} catch (error) {
		console.error('Invalid URL passed to ActionRequiredLink:', url);
		return url;
	}
};

const LINK_ACTION_TYPES: ActionRequiredType[] = [
	'PROVIDE_PAYMENT',
	'CONFIRM_PAYMENT',
	'COMPLETE_PROFILE',
];

export const getActionRequiredButtonLabel = (item: ActionRequired): string => {
	const type = item.action_type;
	const rawName =
		item.name === 'Sign Retainer' ? item.name.split(' ')[1] : item.name;
	const name = rawName.length > 50 ? `${rawName.slice(0, 50)}...` : rawName;
	const prefix = getActionRequiredPrefixByType(type);
	const complement = getActionRequiredComplementByType(type);

	return prefix + ' ' + name + ' ' + complement;
};

const ACTION_LABELS: Record<ActionRequiredType, string> = {
	APPROVAL: 'Review Document',
	OPT_OUT: 'Review Document',
	COMPLAINT: 'Review Document',
	DOCUMENT: 'Upload Document',
	REQUESTED_DOCUMENT: 'Upload Document',
	FOLLOW_UP_DOCUMENT: 'Answer Questions',
	FORM: 'Answer Questions',
	FOLLOW: 'Answer Questions',
	SETTLEMENT_WITH_SIGN: 'Sign Document',
	SETTLEMENT_WITHOUT_SIGN: 'Review Document',
	PAYMENT_WITH_SIGN: 'Sign Document',
	PAYMENT_WITHOUT_SIGN: 'Review Document',
	PROVIDE_PAYMENT: 'Add Payment Details',
	CONFIRM_PAYMENT: 'Confirm Details',
	RETAINER: 'Sign Document',
	SIGNATURE: 'Sign Document',
	COMPLETE_PROFILE: 'Complete Profile',
};

const ANSWER_QUESTIONS_DESCRIPTION =
	'Your legal team needs these answers to continue building your case.';
const UPLOAD_DOCUMENT_DESCRIPTION =
	'Your legal team has requested a document from you.';
const REVIEW_DOCUMENT_DESCRIPTION =
	'This is a copy for your records. No other action is needed beyond confirming you’ve seen it.';
const SIGN_DOCUMENT_DESCRIPTION =
	'Your signature is needed before we can file or proceed.';

const ACTION_DESCRIPTIONS: Partial<Record<ActionRequiredType, string>> = {
	APPROVAL: REVIEW_DOCUMENT_DESCRIPTION,
	OPT_OUT: REVIEW_DOCUMENT_DESCRIPTION,
	PAYMENT_WITHOUT_SIGN: REVIEW_DOCUMENT_DESCRIPTION,
	SETTLEMENT_WITHOUT_SIGN: REVIEW_DOCUMENT_DESCRIPTION,
	FOLLOW_UP_DOCUMENT: ANSWER_QUESTIONS_DESCRIPTION,
	FORM: ANSWER_QUESTIONS_DESCRIPTION,
	FOLLOW: ANSWER_QUESTIONS_DESCRIPTION,
	DOCUMENT: UPLOAD_DOCUMENT_DESCRIPTION,
	REQUESTED_DOCUMENT: UPLOAD_DOCUMENT_DESCRIPTION,
	RETAINER: SIGN_DOCUMENT_DESCRIPTION,
	SETTLEMENT_WITH_SIGN: SIGN_DOCUMENT_DESCRIPTION,
	PAYMENT_WITH_SIGN: SIGN_DOCUMENT_DESCRIPTION,
	SIGNATURE: SIGN_DOCUMENT_DESCRIPTION,
	COMPLAINT: REVIEW_DOCUMENT_DESCRIPTION,
	PROVIDE_PAYMENT:
		'Link Venmo or PayPal. This is where any payment from your case will be sent.',
	CONFIRM_PAYMENT: 'Check that your payment details are correct.',
};

export const getActionRequiredDescription = (
	item: ActionRequired
): string | undefined => {
	if (item.action_type === 'SIGNATURE' && !item.is_signature_required)
		return REVIEW_DOCUMENT_DESCRIPTION;
	return ACTION_DESCRIPTIONS[item.action_type];
};

export const getActionRequiredActionLabel = (item: ActionRequired): string => {
	if (item.action_type === 'SIGNATURE' && !item.is_signature_required)
		return 'Review Document';
	return ACTION_LABELS[item.action_type] ?? 'Open';
};

export const getActionRequiredHref = (
	item: ActionRequired
): string | undefined => {
	const type = item.action_type;

	if (!LINK_ACTION_TYPES.includes(type)) return undefined;

	return getModifiedUrl(
		item.url || '',
		type === 'PROVIDE_PAYMENT' || type === 'CONFIRM_PAYMENT'
			? { fromCaseAction: 'true' }
			: undefined
	);
};

export const getActionRequiredIds = (
	item: ActionRequired
): { caseId?: string; appId?: number } => ({
	caseId: item.case?.case_id ?? item.application?.case_id,
	appId:
		item.case?.application_id ?? item.application?.id ?? item.application_id,
});

export const getDaysRemaining = (dueDate: string): number =>
	differenceInCalendarDays(new Date(dueDate), new Date());

export const isPastDue = (dueDate: string): boolean =>
	getDaysRemaining(dueDate) < 0;

const getDaysPastDue = (dueDate: string): number => -getDaysRemaining(dueDate);

const getDueTime = (item: ActionRequired): number => {
	const time = new Date(item.due_date).getTime();
	return Number.isNaN(time) ? Number.MAX_SAFE_INTEGER : time;
};

export const sortByDueDate = (items: ActionRequired[]): ActionRequired[] =>
	[...items].sort((a, b) => getDueTime(a) - getDueTime(b));

export const getTimerLabelText = (dueDate: string): string => {
	let label = 'days';
	const currentDate = new Date();
	const target = new Date(dueDate);

	let daysRemaining = isPastDue(dueDate)
		? -getDaysPastDue(dueDate)
		: getDaysRemaining(dueDate);

	if (Math.abs(daysRemaining) === 1) label = 'day';
	if (daysRemaining > 28) {
		if (differenceInMonths(currentDate, target) > 0) {
			if (differenceInMonths(currentDate, target) > 1) {
				label = 'months';
				daysRemaining = differenceInMonths(currentDate, target);
			} else {
				label = 'month';
				daysRemaining = 1;
			}
		}
	}

	return `${Math.abs(daysRemaining)} ${label} ${
		isPastDue(dueDate) ? 'past due date' : 'left to complete'
	}`;
};

export const shouldShowTimer = (item: ActionRequired): boolean =>
	!(
		(item.action_type === 'SIGNATURE' ||
			item.action_type === 'COMPLETE_PROFILE' ||
			item.action_type === 'FOLLOW_UP_DOCUMENT') &&
		!item.due_date
	);

const pluralize = (count: number, unit: string): string =>
	`${count} ${unit}${count === 1 ? '' : 's'}`;

export const getDeadlineLabel = (dueDate: string): string => {
	if (isPastDue(dueDate))
		return `${pluralize(getDaysPastDue(dueDate), 'day')} past due date`;

	const daysRemaining = getDaysRemaining(dueDate);

	if (daysRemaining < 14)
		return `${pluralize(daysRemaining, 'day')} left to complete`;
	return `${pluralize(Math.floor(daysRemaining / 7), 'week')} left to complete`;
};
